import { useState } from 'react';
import { C } from '@ridgewill/brand';
import type { Box, BoxAction, BoxVerb, Runner, RunningJob } from '../lib/fleet-api.js';
import type { FleetAlertsState } from '../lib/useFleetAlerts.js';
import {
  actionBlocked,
  actionCopy,
  actionStatusView,
  boxStateView,
  CPU_HOT_C,
  diskTone,
  durationText,
  hasDocker,
  lastLine,
  listenerView,
  runnerName,
  share,
  verbLabel,
} from '../lib/fleet-boxes.js';
import { actorName } from '../lib/fleet-routing.js';
import { count, numeric } from '../lib/format.js';
import { describe, useAction } from '../lib/useAsync.js';
import { useSession } from '../lib/auth-client.js';
import { Button, DotPill, EmptyState, HoldButton, OutcomeNote, When, type Outcome } from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';

/**
 * The CI fleet's box cards and alert banner. org-admin#97, with box actions
 * from org-admin#99.
 *
 * Their own module, fed by props, so the screen fetches and these only draw:
 * CiFleet.tsx passes fleet-api's answers and calls in, and the app shell passes
 * the alerts (lib/useFleetAlerts.ts).
 *
 * ── AN ACTION IS QUEUED, NOT DONE ────────────────────────────────────
 *
 * A box has no public address, so fleet-api hands an action to the box in the
 * answer to its next report (fleet-api#24). The click only queues it: the card
 * then shows it waiting, running, and its result as the box reports them. Each
 * one gets a confirmation (a Modal, as a routing switch does) that says what it
 * costs, which for a restart or a reboot is any job running there.
 */

// ── ALERT BANNER ─────────────────────────────────────────────────────

/** Above every CI fleet tab. Nothing at all when there is nothing to say. */
export function AlertBanner({ state }: { state: FleetAlertsState | null }) {
  if (!state || (state.alerts.length === 0 && state.unchecked.length === 0)) return null;
  const danger = state.alerts.some((a) => a.tone === 'danger');
  const tone = danger
    ? { fg: C.dangerText, bg: C.dangerBg, br: C.dangerBorder }
    : state.alerts.length > 0
      ? { fg: C.warnText, bg: C.warnBg, br: C.warnDot }
      : { fg: C.textMid, bg: C.page, br: C.border };

  return (
    <section aria-label="CI fleet alerts" aria-live="polite" style={{ ...s.banner, color: tone.fg, background: tone.bg, borderColor: tone.br }}>
      {state.alerts.length > 0 ? (
        <>
          <h2 style={s.bannerTitle}>
            {count(state.alerts.length)} alert{state.alerts.length === 1 ? '' : 's'}
          </h2>
          <ul style={s.bannerList}>
            {state.alerts.map((a) => (
              <li key={a.key}>
                <strong>{a.title}.</strong> {a.detail}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {state.unchecked.length > 0 ? (
        <p style={{ margin: 0, fontSize: '.82rem' }}>Could not check {state.unchecked.join(', ')} just now, so an alert there would not show.</p>
      ) : null}
    </section>
  );
}

// ── BOX CARDS ────────────────────────────────────────────────────────

/** Entries shown per card. fleet-api sends up to five. */
const RECENT_SHOWN = 3;

type Choice = { box: Box; verb: BoxVerb; target: string | null };

export function BoxCards({
  boxes,
  runners,
  running,
  requestAction,
  cancelAction,
  onChanged,
}: {
  boxes: Box[];
  runners: Runner[] | null;
  /** The collector's running jobs, or null when unread, so a confirmation can name the jobs an action would lose. */
  running: RunningJob[] | null;
  requestAction: (box: string, verb: BoxVerb, target?: string) => Promise<BoxAction>;
  cancelAction: (id: number) => Promise<BoxAction>;
  /** Refetch the boxes, after an action is queued or cancelled. */
  onChanged: () => void;
}) {
  // `user?.`: as in CiRouting, a session answer without a user costs the "you" label, not the cards.
  const { data: session } = useSession();
  const currentUserId = session?.user?.id ?? null;
  const [choosing, setChoosing] = useState<Choice | null>(null);
  /** The latest outcome, on the card it belongs to. */
  const [note, setNote] = useState<{ box: string; outcome: Outcome } | null>(null);
  const [cancelling, setCancelling] = useState<number | null>(null);

  if (boxes.length === 0) {
    return <EmptyState title="No boxes are reporting" suggestion="A box reports once its fleet agent is installed with a token (standards/self-hosted-ci-runners.md in docs)." />;
  }
  const githubStatus = new Map((runners ?? []).map((r) => [r.name, r.github_status]));

  const cancel = (a: BoxAction) => {
    setCancelling(a.id);
    setNote(null);
    cancelAction(a.id)
      .then(
        () => {
          setNote({ box: a.box, outcome: { ok: true, message: `Cancelled ${verbLabel(a.verb, a.target)}.` } });
          onChanged();
        },
        (e: unknown) => setNote({ box: a.box, outcome: { ok: false, message: describe(e) } }),
      )
      .finally(() => setCancelling(null));
  };

  return (
    <>
      <div style={s.grid}>
        {boxes.map((box) => (
          <BoxCard
            key={box.name}
            box={box}
            githubStatus={githubStatus}
            currentUserId={currentUserId}
            outcome={note?.box === box.name ? note.outcome : null}
            cancelling={cancelling}
            onChoose={(verb, target) => {
              setNote(null);
              setChoosing({ box, verb, target });
            }}
            onCancel={cancel}
          />
        ))}
      </div>
      {choosing ? (
        <ConfirmAction
          choice={choosing}
          running={running}
          requestAction={requestAction}
          onClose={() => setChoosing(null)}
          onDone={(message) => {
            setNote({ box: choosing.box.name, outcome: { ok: true, message } });
            setChoosing(null);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

const gb = (mb: number | null) => (mb === null ? '—' : (mb / 1024).toFixed(1));

function BoxCard({
  box,
  githubStatus,
  currentUserId,
  outcome,
  cancelling,
  onChoose,
  onCancel,
}: {
  box: Box;
  githubStatus: Map<string, string>;
  currentUserId: string | null;
  outcome: Outcome;
  cancelling: number | null;
  onChoose: (verb: BoxVerb, target: string | null) => void;
  onCancel: (action: BoxAction) => void;
}) {
  const state = boxStateView(box);
  const r = box.last_report;
  const current = box.state === 'reporting';
  const disk = r ? share(r.disk_used_gb, r.disk_total_gb) : null;
  const memory = r ? share(r.mem_used_mb, r.mem_total_mb) : null;
  const blocked = actionBlocked(box);
  const recent = (box.actions ?? []).slice(0, RECENT_SHOWN);

  return (
    <article style={s.card} aria-label={`${box.name}, ${state.label}`}>
      <header style={s.cardHead}>
        <span style={s.boxName}>{box.name}</span>
        <DotPill tone={state.tone}>{state.label}</DotPill>
      </header>

      {r ? (
        <>
          <p style={s.cardSub}>
            {/* A silent box's numbers are its last, and say so first. */}
            {current ? 'Reported' : 'Last report'} <When iso={r.received_at} />
            {r.hostname && r.hostname !== box.name ? ` · calls itself ${r.hostname}` : ''}
          </p>
          <dl style={{ ...s.metrics, opacity: current ? 1 : 0.6 }}>
            <Metric label="Uptime" value={durationText(r.uptime_s)} />
            <Metric
              label="CPU"
              value={r.cpu_temp_c === null ? '—' : `${Math.round(r.cpu_temp_c)} °C`}
              danger={r.cpu_temp_c !== null && r.cpu_temp_c > CPU_HOT_C}
            />
            <Metric label="Load" value={r.load_1 === null ? '—' : r.load_1.toFixed(2)} />
            <Metric
              label="Memory"
              value={r.mem_used_mb === null ? '—' : `${gb(r.mem_used_mb)} of ${gb(r.mem_total_mb)} GB`}
              note={memory === null ? undefined : `${Math.round(memory * 100)}%`}
            />
            <div style={s.metricWide}>
              <dt style={s.metricLabel}>Disk</dt>
              <dd style={s.metricValue}>
                <span style={numeric}>
                  {r.disk_used_gb === null ? '—' : `${r.disk_used_gb.toFixed(1)} of ${r.disk_total_gb?.toFixed(0)} GB`}
                </span>
                {disk !== null ? (
                  <span
                    role="meter"
                    aria-label="Disk used"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(disk * 100)}
                    style={s.bar}
                  >
                    <span style={{ ...s.barFill, width: `${Math.round(disk * 100)}%`, background: diskTone(disk) === 'warn' ? C.warnDot : C.successDot }} />
                  </span>
                ) : null}
              </dd>
            </div>
            {hasDocker(r) ? <Metric label="Docker" value={`${r.docker_gb!.toFixed(1)} GB`} /> : null}
          </dl>

          <ul style={s.runners} aria-label={`Runners on ${box.name}`}>
            {r.runners.length === 0 ? <li style={{ color: C.muted, fontSize: '.8rem' }}>No runner units reported</li> : null}
            {r.runners.map((runner) => {
              const name = runnerName(runner.unit);
              const view = listenerView(runner, githubStatus.get(name));
              return (
                <li key={runner.unit} style={s.runner}>
                  <span style={s.row}>
                    <span style={s.runnerName}>{name}</span>
                    <span style={s.rowEnd}>
                      {/* A silent box's runners are as they were at its last report, not now. */}
                      <DotPill tone={current ? view.tone : 'muted'}>{current ? view.label : `At last report: ${view.label}`}</DotPill>
                      <Button tone="danger" onClick={() => onChoose('restart_runner', name)} disabled={blocked !== null} title={blocked ?? `Restart the ${name} runner service`}>
                        Restart
                      </Button>
                    </span>
                  </span>
                  {runner.last_line ? (
                    <span style={s.runnerLine} title={runner.last_line}>
                      {runner.last_line}
                      {runner.last_line_at ? (
                        <>
                          {' · '}
                          <When iso={runner.last_line_at} />
                        </>
                      ) : null}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <p style={s.cardSub}>
          {box.state === 'revoked' ? 'Its token was revoked, so its reports are refused.' : 'No report has arrived since its token was issued.'}
        </p>
      )}

      <section style={s.actions} aria-label={`Actions on ${box.name}`}>
        <div style={s.actionButtons}>
          <Button onClick={() => onChoose('report', null)} disabled={blocked !== null} title={blocked ?? 'Ask the box for a fresh report'}>
            Send a report
          </Button>
          {hasDocker(box.last_report) ? (
            <Button onClick={() => onChoose('prune_docker', null)} disabled={blocked !== null} title={blocked ?? "Run the weekly Docker prune now"}>
              Prune Docker
            </Button>
          ) : null}
          <Button tone="danger" onClick={() => onChoose('reboot', null)} disabled={blocked !== null} title={blocked ?? 'Reboot the box'}>
            Reboot
          </Button>
        </div>
        <OutcomeNote outcome={outcome} />
        {recent.length > 0 ? (
          <ul style={s.actionList} aria-label={`Recent actions on ${box.name}`}>
            {recent.map((a) => {
              const view = actionStatusView(a);
              const by = actorName(a.requested_by, currentUserId);
              const line = lastLine(a.output);
              return (
                <li key={a.id} style={s.runner}>
                  <span style={s.row}>
                    <span style={s.actionName}>{verbLabel(a.verb, a.target)}</span>
                    <span style={s.rowEnd}>
                      <DotPill tone={view.tone}>{view.label}</DotPill>
                      {a.status === 'queued' ? (
                        <Button onClick={() => onCancel(a)} disabled={cancelling !== null}>
                          {cancelling === a.id ? 'Cancelling...' : 'Cancel'}
                        </Button>
                      ) : null}
                    </span>
                  </span>
                  <span style={s.runnerLine} title={a.output ?? undefined}>
                    <When iso={a.completed_at ?? a.claimed_at ?? a.requested_at} /> · requested by {by === 'You' ? 'you' : by}
                    {line ? ` · ${line}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </article>
  );
}

function ConfirmAction({
  choice,
  running,
  requestAction,
  onClose,
  onDone,
}: {
  choice: Choice;
  running: RunningJob[] | null;
  requestAction: (box: string, verb: BoxVerb, target?: string) => Promise<BoxAction>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const action = useAction();
  const { box, verb, target } = choice;
  const copy = actionCopy(box, verb, target, running);
  const destructive = verb === 'reboot' || verb === 'restart_runner';
  const confirm = () => {
    let queued = false;
    void action
      .run(async () => {
        await requestAction(box.name, verb, target ?? undefined);
        queued = true;
        return copy.done;
      })
      // Only a queued action closes the dialog, so a refusal (another action
      // already in flight, a runner the box no longer reports) stays beside the
      // button that caused it.
      .then(() => {
        if (queued) onDone(copy.done);
      });
  };

  return (
    <Modal
      title={copy.title}
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          {destructive ? (
            <HoldButton
              disabled={action.running}
              title="Hold for a second and a half to confirm. Any job running there is lost."
              onHold={confirm}
            >
              {action.running ? 'Queueing...' : copy.button}
            </HoldButton>
          ) : (
            <Button disabled={action.running} onClick={confirm}>
              {action.running ? 'Queueing...' : copy.button}
            </Button>
          )}
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <ul style={s.points}>
        {copy.points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </Modal>
  );
}

function Metric({ label, value, note, danger }: { label: string; value: string; note?: string; danger?: boolean }) {
  return (
    <div style={s.metric}>
      <dt style={s.metricLabel}>{label}</dt>
      <dd style={{ ...s.metricValue, color: danger ? C.dangerText : C.text }}>
        <span style={numeric}>{value}</span>
        {note ? <span style={{ color: C.muted, fontSize: '.75rem' }}> {note}</span> : null}
      </dd>
    </div>
  );
}

const s = {
  banner: { display: 'grid', gap: '.4rem', padding: '.8rem 1rem', border: '1px solid', borderRadius: 10 },
  bannerTitle: { margin: 0, fontSize: '.9rem', fontWeight: 700 },
  bannerList: { margin: 0, paddingLeft: '1.1rem', display: 'grid', gap: '.25rem', fontSize: '.85rem', lineHeight: 1.45 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 290px), 1fr))', gap: '.75rem', padding: '1rem 1.1rem' },
  card: { display: 'grid', gap: '.6rem', alignContent: 'start', padding: '.85rem .95rem', background: C.page, border: `1px solid ${C.border}`, borderRadius: 10, minWidth: 0 },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' as const },
  boxName: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700, fontSize: '.92rem' },
  cardSub: { margin: 0, fontSize: '.78rem', color: C.muted },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '.45rem .9rem', margin: 0 },
  metric: { minWidth: 0 },
  metricWide: { gridColumn: '1 / -1', minWidth: 0 },
  metricLabel: { fontSize: '.7rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase' as const, letterSpacing: '.04em' },
  metricValue: { margin: 0, fontSize: '.85rem', display: 'grid', gap: '.25rem' },
  bar: { display: 'block', height: 6, borderRadius: 3, background: C.surface, border: `1px solid ${C.border}`, overflow: 'hidden' },
  barFill: { display: 'block', height: '100%' },
  runners: { listStyle: 'none', margin: 0, padding: '.55rem 0 0', borderTop: `1px solid ${C.border}`, display: 'grid', gap: '.5rem' },
  runner: { display: 'grid', gap: '.15rem', minWidth: 0 },
  row: { display: 'flex', justifyContent: 'space-between', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' as const },
  rowEnd: { display: 'inline-flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' as const },
  runnerName: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '.8rem', fontWeight: 600 },
  runnerLine: { fontSize: '.75rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  actions: { display: 'grid', gap: '.55rem', padding: '.55rem 0 0', borderTop: `1px solid ${C.border}`, minWidth: 0 },
  actionButtons: { display: 'flex', gap: '.5rem', flexWrap: 'wrap' as const },
  actionList: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '.5rem' },
  actionName: { fontSize: '.8rem', fontWeight: 600 },
  points: { margin: 0, paddingLeft: '1.1rem', display: 'grid', gap: '.45rem', lineHeight: 1.5, fontSize: '.88rem' },
};
